import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, signInWithEmailAndPassword, signOut, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { AuthProfile } from '../types/auth';

interface AuthContextType {
  user: User | null;
  authProfile: AuthProfile | null;
  adminType: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authProfile, setAuthProfile] = useState<AuthProfile | null>(null);
  const [adminType, setAdminType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 세션 지속성 설정
    setPersistence(auth, browserLocalPersistence);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // 관리자 권한 확인
          const profileDoc = await getDoc(doc(db, 'auth_profiles', firebaseUser.uid));

          if (profileDoc.exists()) {
            const profile = { uid: firebaseUser.uid, ...profileDoc.data() } as AuthProfile;

            if (profile.isAdmin) {
              setUser(firebaseUser);
              setAuthProfile(profile);
              setAdminType(profile.adminType);
            } else {
              // 관리자가 아닌 경우
              setUser(null);
              setAuthProfile(null);
              setAdminType(null);
              await signOut(auth);
            }
          } else {
            // 프로필이 없는 경우
            setUser(null);
            setAuthProfile(null);
            setAdminType(null);
            await signOut(auth);
          }
        } catch (error) {
          console.error('Error fetching auth profile:', error);
          setUser(null);
          setAuthProfile(null);
          setAdminType(null);
        }
      } else {
        setUser(null);
        setAuthProfile(null);
        setAdminType(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setAuthProfile(null);
      setAdminType(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, authProfile, adminType, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
